import { useState } from "react";
import { toast } from "react-toastify";
import PayrollRunEmployeesInRun from "./PayrollRunEmployeesInRun";
import PayslipDetailModal from "features/Payroll/components/PayslipDetailModal";
import PayrollItemAdjustmentsModal from "features/Payroll/components/PayrollItemAdjustmentsModal";
import PayPayrollItemModal from "features/Payroll/components/PayPayrollItemModal";
import {
  isPayrollRunDraft,
  isPayrollRunFinalized,
} from "features/Payroll/constants/payroll.constants";
import { payPayrollItem } from "features/Payroll/services/PayrollService";
import { downloadPayrollItemReceipt } from "features/Payroll/utils/payrollItemReceipt";
import type { PayrollItem } from "features/Payroll/types/payroll.types";
import { usePayrollRunDetail } from "./PayrollRunDetailContext";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { withLoadingToast } from "utils/withLoadingToast";

const EmployeesTab = () => {
  const { can } = useAccess();
  const canPay = can(Permissions.PAYROLL.DISBURSE);
  const { run, lineItems, totals, refetch } = usePayrollRunDetail();
  const [slipItemId, setSlipItemId] = useState<number | null>(null);
  const [adjustItem, setAdjustItem] = useState<PayrollItem | null>(null);
  const [payItem, setPayItem] = useState<PayrollItem | null>(null);
  const [paySubmitting, setPaySubmitting] = useState(false);

  if (!run) return null;

  const handlePay = async (payload: {
    modeOfPaymentId?: number;
    paymentDate?: string | null;
    referenceNumber?: string | null;
  }) => {
    if (!payItem) return;
    setPaySubmitting(true);
    try {
      const res = await payPayrollItem(payItem.id, payload);
      if (res.success) {
        toast.success(res.message ?? "Salary paid.");
        setPayItem(null);
        await refetch();
      } else {
        toast.error(res.message ?? "Could not pay salary.");
      }
    } catch (err) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Could not pay salary.",
      );
    } finally {
      setPaySubmitting(false);
    }
  };

  const handleViewReceipt = async (item: PayrollItem) => {
    await withLoadingToast(
      "Preparing receipt…",
      () => downloadPayrollItemReceipt(item),
      "Receipt downloaded",
      "Could not download receipt",
      (e) => (e instanceof Error ? e.message : "Could not download receipt"),
    );
  };

  return (
    <>
      <PayslipDetailModal
        isOpen={slipItemId != null}
        onClose={() => setSlipItemId(null)}
        itemId={slipItemId}
      />
      <PayrollItemAdjustmentsModal
        isOpen={adjustItem != null}
        onClose={() => setAdjustItem(null)}
        item={adjustItem}
        onSaved={() => void refetch()}
      />
      <PayPayrollItemModal
        show={payItem != null}
        item={payItem}
        submitting={paySubmitting}
        onCloseClick={() => setPayItem(null)}
        onConfirm={(payload) => void handlePay(payload)}
      />
      <PayrollRunEmployeesInRun
        items={lineItems}
        totals={totals}
        onViewSlip={isPayrollRunFinalized(run.status) ? setSlipItemId : undefined}
        onAdjust={isPayrollRunDraft(run.status) ? setAdjustItem : undefined}
        onPay={canPay ? setPayItem : undefined}
        onViewReceipt={(item) => void handleViewReceipt(item)}
      />
    </>
  );
};

export default EmployeesTab;
